"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcHvac, type HvacResult, type HvacClimate, type HvacEfficiency, type HvacSystemType, HVAC_SQFT_PER_TON } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const CLIMATE_OPTIONS = [
  { value: "cold",     label: "Cold — Minneapolis, Chicago, Denver" },
  { value: "moderate", label: "Moderate — Nashville, Atlanta, Dallas" },
  { value: "hot_humid", label: "Hot / Humid — Houston, Miami, Phoenix" },
  { value: "hot_dry",  label: "Hot / Dry — Phoenix, Las Vegas, Tucson" },
];
const EFFICIENCY_OPTIONS = [
  { value: "standard",  label: `Standard — 14–16 SEER2 (minimum code)` },
  { value: "high",      label: "High Efficiency — 18–20 SEER2" },
  { value: "premium",   label: "Premium — 21+ SEER2 / Heat Pump" },
];
const SYSTEM_OPTIONS = [
  { value: "central",   label: "Central Split System (AC + gas/electric furnace)" },
  { value: "heat_pump", label: "Heat Pump Split System (heating + cooling)" },
  { value: "minisplit", label: "Mini-Split (ductless, 1–4 zones)" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [sqft, setSqft] = useState("");
  const [climate, setClimate] = useState<HvacClimate>("moderate");
  const [efficiency, setEfficiency] = useState<HvacEfficiency>("standard");
  const [systemType, setSystemType] = useState<HvacSystemType>("central");
  const [replaceDucts, setReplaceDucts] = useState(false);
  const [result, setResult] = useState<HvacResult | null>(null);

  function handleCalculate() {
    setResult(calcHvac({
      sqft: parseFloat(sqft) || 0,
      climate,
      efficiency,
      systemType,
      replaceDucts,
    }));
  }

  const spec = CALCULATOR_SPECS.hvac;

  return (
    <CalculatorShell
      name="HVAC Replacement Cost Calculator"
      description="Estimate the cost to replace a central AC, heat pump, or mini-split system based on home size, climate, and efficiency tier."
      icon="hvac"
      category="high-value"
      accentColor="#38bdf8"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="HVAC pricing varies significantly by region, local labor rates, and equipment availability. Always get 3+ quotes from licensed HVAC contractors."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="hvac-sqft" label="Conditioned Home Size" unit="sq ft" type="number" min="0" step="50" placeholder="1800" value={sqft} onChange={(e) => { setSqft(e.target.value); setResult(null); }} hint={`Rule of thumb: ~${HVAC_SQFT_PER_TON} sq ft per ton. Actual sizing requires ACCA Manual J.`} />
          <SelectField id="climate" label="Climate Zone" value={climate} onChange={(e) => { setClimate(e.target.value as HvacClimate); setResult(null); }} options={CLIMATE_OPTIONS} />
          <SelectField id="system-type" label="System Type" value={systemType} onChange={(e) => { setSystemType(e.target.value as HvacSystemType); setResult(null); }} options={SYSTEM_OPTIONS} hint="Heat pumps qualify for up to $2,000 federal tax credit (IRA Sec. 25C)" />
          <SelectField id="efficiency" label="Efficiency Tier" value={efficiency} onChange={(e) => { setEfficiency(e.target.value as HvacEfficiency); setResult(null); }} options={EFFICIENCY_OPTIONS} hint="Min. SEER2 15 required in South/Southwest US as of 2023" />
          <CheckboxField id="replace-ducts" label="Replace / add ductwork" hint="New ductwork adds $3,000–$8,000 to installation cost" checked={replaceDucts} onChange={(v) => { setReplaceDucts(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="System Size" value={result.estimatedTons} unit="tons" accent size="md" sub={`${(result.estimatedTons * 12000).toLocaleString()} BTU/h`} />
              <ResultCard label="SEER2 Tier" value={efficiency === "standard" ? "14–16" : efficiency === "high" ? "18–20" : "21+"} accent sub="Efficiency rating" />
            </div>
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Total Installed Cost Range</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>midpoint {fmt(result.costMidpoint)} · ±20% regional variance</p>
            </div>
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Cost Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: `Equipment + labor (${result.estimatedTons}-ton)`, value: fmt(result.costMin) },
                  ...(result.ductCost > 0 ? [{ label: "Ductwork", value: fmt(result.ductCost) }] : []),
                  { label: "Total midpoint", value: fmt(result.costMidpoint), bold: true },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                      row.bold
                        ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                        : "border-l-3 border-l-transparent"
                    }`}
                  >
                    <span className={`text-[12.5px] font-sans ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                      {row.label}
                    </span>
                    <span className={`font-mono text-[13px] ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {systemType === "heat_pump" && (
              <div className="info-box flex items-start gap-2">
                <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
                <span><strong>IRA Tax Credit:</strong> Qualified heat pumps may qualify for up to $2,000 federal tax credit under IRA Section 25C. Verify with your tax advisor.</span>
              </div>
            )}
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">SEER2 Ratings &amp; Efficiency Standards</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Choosing the right efficiency tier is one of the most important decisions in an <Link href="/articles/hvac-replacement-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">HVAC replacement</Link>. As of January 2023, the DOE mandates a minimum of <strong>SEER2 {spec.buyingGuide.seerRatingMinimum2026.value}</strong> for new residential split-system air conditioners in the Southeast and Southwest US — up from the previous 14 SEER standard. Upgrading from a 14 SEER unit to a 20 SEER system reduces cooling energy consumption by roughly 30%, which can save $200–$400 annually on electricity bills. But will it actually lower your bill? Read our guide on <Link href="/articles/will-new-ac-lower-electric-bill/" className="text-[var(--accent)] hover:underline font-semibold">whether a new AC reduces electric bills</Link> to learn how insulation and ductwork affect real-world savings. Ultra-high-efficiency <Link href="/articles/heat-pump-vs-central-ac-comparison/" className="text-[var(--accent)] hover:underline font-semibold">heat pumps</Link> now reach 25+ SEER2 and qualify for up to $2,000 in federal tax credits under the Inflation Reduction Act, significantly shortening their payback period.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/hvac_buying_guide.webp" alt="Modern high-efficiency heat pump system installed on the exterior of a residential home" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/hvac_site_prep.webp" alt="HVAC technician sizing a new system unit outdoors, checking clearance requirements" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Correct Sizing &amp; Installation Practices</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Oversized HVAC systems are as problematic as undersized ones. An oversized unit short-cycles — <Link href="/articles/signs-your-ac-is-dying-repair-vs-replace/" className="text-[var(--accent)] hover:underline font-semibold">turning on and off too frequently</Link> — which prevents effective dehumidification, accelerates wear, and creates uncomfortable temperature swings. The only code-compliant way to properly size a system is through an <strong>ACCA Manual J</strong> load calculation, which accounts for your home&apos;s insulation, window area, ceiling height, air infiltration rate, local climate, and occupancy. Beyond sizing, the outdoor unit requires <strong>{spec.sitePrep.clearanceRequirements.value}</strong> for proper airflow and service access. Obstructed clearances can reduce system efficiency by up to 10% and void manufacturer warranties.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.ductLeakageTolerance.standardCode}
        sourceName={spec.industryStandards.ductLeakageTolerance.sourceName}
        sourceUrl={spec.industryStandards.ductLeakageTolerance.sourceUrl}
        paragraphs={[
          <p key="p1">
            One of the most overlooked factors in HVAC performance is duct leakage. The DOE&apos;s ENERGY STAR program requires that <b>total duct leakage not exceed {spec.industryStandards.ductLeakageTolerance.value}</b>. In practice, studies by the Lawrence Berkeley National Laboratory found that typical American homes lose 20–30% of their conditioned air through leaks in attic ductwork — effectively paying to heat and cool unconditioned space. This single issue can negate the efficiency gains of upgrading to a high-SEER system entirely.
          </p>,
          <p key="p2">
            When replacing an HVAC system, always request a duct leakage test (blower door + duct blaster) from your contractor. Sealing and insulating existing ducts can improve system efficiency by 20–30% and is now required by code in many jurisdictions before a new system can be commissioned. In homes without existing ductwork, a ductless mini-split system eliminates leakage entirely, delivering conditioned air directly to each zone at near-100% efficiency.
          </p>
        ]}
        keyRequirements={[
          "Total duct leakage must not exceed 4 CFM25 per 100 sq ft of floor area.",
          "Perform blower door and duct blaster tests during system commissioning.",
          "Insulate attic ductwork to at least R-8 to minimize thermal loss.",
          "Ductless mini-splits can be utilized to eliminate duct leakage entirely."
        ]}
      />

      {/* Featured Guide Banner */}
      <div className="my-8 p-5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-6 card-blueprint text-left">
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-[9px] font-mono text-[var(--accent)] uppercase font-bold tracking-wider">Featured HVAC Guide</span>
          <h4 className="text-base font-bold text-[var(--ink)] m-0">HVAC Replacement Cost Guide: Heating & AC (2026 Prices)</h4>
          <p className="text-xs text-[var(--ink-soft)] leading-relaxed m-0">
            Compare prices for heat pumps, central ACs, and furnaces, understand duct repair costs, and read tips to avoid contractor overcharges.
          </p>
        </div>
        <Link 
          href="/articles/hvac-replacement-cost-guide/" 
          className="px-4 py-2 bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90 text-xs font-mono font-bold uppercase tracking-wider rounded transition-all shrink-0 cursor-pointer text-center"
        >
          Read HVAC Guide &rarr;
        </Link>
      </div>

      <FaqSection calculatorId="hvac" />
      <RelatedCalculators calculatorId="hvac" />
    </CalculatorShell>
  );
}
